import React, { useCallback, useMemo, useEffect, useState } from "react";
import _, { isString } from "lodash";
import { toast } from "react-toastify";
import { useApolloClient } from "@apollo/client";

import { useRouter } from "../../../../lib/hooks/useRouter";
import { useRoomFormik } from "../../../shared/Forms/RoomForm/lib/useRoomFormik";
import { useWorkplaceRequests } from "../../../../api/graphql/useWorkplaceRequests";
import { useCustomerContext } from "../../../../lib/context/Customer/CustomerContext";
import { useCalendarContext } from "../../../../lib/context/Calendar/CalendarContext";
import { useDisplaySettingsFormik } from "../../../shared/Forms/DisplaySettingsForm/lib/useDisplaySettingsForm";
import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import { useCustomerPermissions } from "../../../Billings/lib/useCustomerPermissions";
import { useWorkingHoursData } from "../../../Account/helpers/useWorkingHoursData";
import { useDebounce } from "../../../../lib/hooks/useDebounce";
import { useDefineLicense } from "../../../../lib/hooks/useDefineLicense";
import { useWorkplaceData } from "../../../../lib/hooks/useWorkplaceData";

import {
  Amenity,
  RoomFields,
  RoomRequestVariables,
  ROOM_FIELDS_FRAGMENT,
  GET_ROOM,
  UpdateRoomDisplaySettingsVariables,
} from "../../../../api/graphql/rooms/rooms";

import { SubRouteProps } from "../../../../lib/routes/routes";

import {
  RoomForm,
  roomAmenetiesNames,
} from "../../../shared/Forms/RoomForm/RoomForm";
import { tooltips } from "../../../../lib/utils/tooltips";
import { EditSettings } from "../EditSettings/EditSettings";
import { EditRoomViews } from "./EditRoomViews/EditRoomViews";
import { WorkplaceListData } from "../../WorkplaceList/WorkplaceList";
import { EditWorkplaceWrapper } from "../EditWorkplaceWrapper/EditWorkplaceWrapper";
import { DisplaySettingsForm } from "../../../shared/Forms/DisplaySettingsForm/DisplaySettingsForm";
import { EditWorkplaceProps } from "../../../../views/Manage/Workplaces/EditWorkplace/EditWorkplace";
import { DownloadQRCode } from "../../../DownloadQRCode/DownloadQRCode";
import {
  ListTagsEntity,
  SensorEntitlement,
} from "../../../../api/grpc/workplaces/workplaces";
import { format } from "date-fns";
import { dateHelper } from "../../../shared/Forms/DisplaySettingsForm/lib/displayDateHelper";
import { CalendarType } from "../../../../api/grpc/calendarprovider/calendarprovider";
import { EntityType } from "../../../../api/grpc/subscription/subscription";
import { getBookingWindowValue } from "../../../../api/graphql/workplaces";

export const EditRoom = (props: EditWorkplaceProps) => {
  const { id } = props;
  const router = useRouter();
  const client = useApolloClient();
  const { user } = useAuthContext();
  const { providers } = useCalendarContext();
  const { customer } = useCustomerContext();
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingDisplaySettings, setLoadingDisplaySettings] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedValue = useDebounce<string>(searchValue, 500);

  const { updateRoom, updateRoomDisplay } = useWorkplaceRequests();
  const [roomData, setRoomData] = useState<RoomFields | null>(null);
  const {
    data: customerPermissions,
    error: customerError,
    isBussinesOrPro,
  } = useCustomerPermissions();
  const { error, loading, customerSettings, handleWorkingHours, refetch } =
    useWorkingHoursData();
  const hasCrossOrgCalendarEntitlement =
    !!customerSettings?.crossOrgRoomCalendarEnabled;

  const { defineLicenseNumber, refetch: refetchLicenseNumber } =
    useDefineLicense({
      type: EntityType.ROOM,
      adjustLeft: true,
      isLicensed: roomData?.licensed,
    });

  // TO DO: Move on how the data are loaded to a hook useWorkplaceData
  const updateData = useCallback(() => {
    const data = client.readFragment<RoomFields>({
      id: `Room:${id}`,
      fragmentName: "RoomFields",
      fragment: ROOM_FIELDS_FRAGMENT,
    });

    if (data) {
      setRoomData(data);
    }
  }, [id, client]);

  // Use the useWorkplaceData hook for initial fetch if not in cache
  const { loading: loadingRoomData } = useWorkplaceData<RoomFields>({
    id,
    typename: "Room",
    fragment: ROOM_FIELDS_FRAGMENT,
    fragmentName: "RoomFields",
    query: GET_ROOM,
    onDataUpdate: setRoomData,
  });

  useEffect(() => {
    updateData();
  }, [id, customer, updateData]);

  const calendarType = useMemo(() => {
    const provider = providers.find(
      (item) => item.iD === roomData?.calendarProviderId
    );

    if (!provider) {
      return undefined;
    }

    return provider.calType;
  }, [roomData]);

  const crossOrganizationIntegration =
    roomData?.crossOrganizationIntegration?.map((integration, index) => ({
      ...integration,
      order: integration.order || index + 1, // Normalize order to start from 1
    })) || [];

  const formik = useRoomFormik({
    initialValues: {
      customerId: user?.customerid || "",
      name: roomData?.name || "",
      description: roomData?.description || "",
      tags: roomData?.tags || [],
      roomAmenities: _.keys(
        _.pickBy(roomData?.amenities, (item) => {
          return item === true;
        })
      ) as Amenity[],
      brokenAmenitiesReporting: roomData?.brokenAmenitiesReporting || false,
      licensed: !!roomData?.licensed,
      numberOfSeats: roomData?.numberOfSeats || 1,
      calendarProviderActive: roomData?.calendarProviderActive || false,
      isBooked: roomData?.isBooked,
      isBlocked: roomData?.isBlocked || false,
      bookingWindow: getBookingWindowValue(roomData?.bookingWindow),
      marker: {
        latitude: roomData?.marker?.latitude || 0,
        longitude: roomData?.marker?.longitude || 0,
      },
      circleShape: {
        latitude: roomData?.circleShape?.latitude || 0,
        longitude: roomData?.circleShape?.longitude || 0,
        radius: roomData?.circleShape?.radius || 0,
        labelAlignment: roomData?.circleShape?.labelAlignment || "center",
      },
      polygonal: roomData?.polygonal?.length
        ? roomData.polygonal.map((item) => ({
            latitude: item.latitude,
            longitude: item.longitude,
            type: item.type,
            id: item.id,
            labelAlignment: item.labelAlignment,
          }))
        : [],
      // we check if roomDirection === null because the existing rooms
      // didn't have the roomDirection object (it's a new field)
      // so we add it with 0 values
      roomDirection: roomData?.roomDirection?.length
        ? roomData.roomDirection
        : [
            {
              direction: 0,
              distance: 0,
            },
          ],
      showCheckInTime: roomData?.showCheckInTime || false,
      checkInTime: roomData?.checkInTime || 1,
      calendarId: roomData?.calendarId || "",
      calendarType: calendarType,
      calendarProviderId: roomData?.calendarProviderId || "",
      reportingEmail: "",
      resourceEmail: roomData?.resourceEmail || "",
      isManualCalendar: roomData?.isManualCalendar || false,
      crossOrganizationIntegration: crossOrganizationIntegration,
      privateCrossOrganizationEvent:
        roomData?.privateCrossOrganizationEvent ?? true,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoadingSettings(true);

        const removedTags =
          roomData?.tags.filter((item) => !values.tags?.includes(item)) || [];

        // Reconcile cross-org integrations by ID instead of replacing every record:
        // existing IDs are retained, changed records are updated, new records are
        // created without an ID, and IDs missing from the form are deleted.
        const submittedCrossOrganizationIntegrations =
          values.crossOrganizationIntegration || [];
        const submittedCrossOrganizationIntegrationIds = new Set(
          submittedCrossOrganizationIntegrations
            .map((integration) => integration.id)
            .filter((integrationId): integrationId is string =>
              Boolean(integrationId)
            )
        );
        const removedCrossOrganizationIntegration =
          roomData?.crossOrganizationIntegration
            ?.filter(
              (integration) =>
                integration.id &&
                !submittedCrossOrganizationIntegrationIds.has(integration.id)
            )
            .map((integration) => ({ id: integration.id as string })) || [];
        const existingCrossOrganizationIntegrations = new Map(
          roomData?.crossOrganizationIntegration
            ?.filter((integration) => integration.id)
            .map((integration) => [integration.id as string, integration]) || []
        );
        const changedCrossOrganizationIntegrations =
          submittedCrossOrganizationIntegrations
            .filter((integration) => {
              if (!integration.id) {
                return false;
              }

              const existing = existingCrossOrganizationIntegrations.get(
                integration.id
              );

              return (
                !!existing &&
                (existing.calendarId !== integration.calendarId ||
                  existing.calendarProviderId !==
                    integration.calendarProviderId ||
                  (existing.resourceEmail || "") !==
                    (integration.resourceEmail || "") ||
                  existing.order !== integration.order)
              );
            })
            .map((integration) => ({
              ...integration,
              id: integration.id as string,
              tenantId: customer.tenantId,
            }));

        // TO DO: Generalize the process for amenities & validation here & on AddRoomModal
        const amenities = roomAmenetiesNames.reduce((acc: any, cur) => {
          acc[cur] = values.roomAmenities.includes(cur);

          return acc;
        }, {});

        const variables: RoomRequestVariables = {
          id,
          name: values.name,
          tags: values.tags || [],
          description: values.description,
          tenantId: customer.tenantId,
          calendarId: values.calendarId,
          numberOfSeats: values.numberOfSeats,
          calendarProviderActive: values.calendarProviderActive,
          isBooked: values.isBooked,
          isBlocked: values.isBlocked,
          bookingWindow: values.bookingWindow,
          marker: {
            latitude: values.marker.latitude,
            longitude: values.marker.longitude,
          },
          circleShape: {
            latitude: values.circleShape.latitude,
            longitude: values.circleShape.longitude,
            radius: values.circleShape.radius,
            labelAlignment: values.circleShape.labelAlignment,
          },
          polygonal: values.polygonal.length
            ? values.polygonal.map((item) => ({
                latitude: item.latitude,
                longitude: item.longitude,
                type: item.type,
                id: item.id,
                labelAlignment: item.labelAlignment,
              }))
            : [],
          roomDirection: values.roomDirection.length
            ? values.roomDirection
            : [
                {
                  distance: 0,
                  direction: 0,
                },
              ],
          showCheckInTime: values.showCheckInTime || false,
          checkInTime: values.checkInTime || 1,
          amenities: {
            ...amenities,
            // reportingEmail: values.reportingEmail,
          },
          brokenAmenitiesReporting: values.brokenAmenitiesReporting,
          calendarProviderId: values.calendarProviderId,
          remove: {
            tags: removedTags,
            crossOrganizationIntegration: removedCrossOrganizationIntegration,
          },
          resourceEmail:
            values.calendarType === CalendarType.EWS
              ? values.calendarId
              : values.resourceEmail,
          isManualCalendar: values.isManualCalendar,
          // Only ID-less entries are nested creates. Existing entries stay linked
          // to the room and are updated separately through their stable IDs.
          crossOrganizationIntegration: submittedCrossOrganizationIntegrations
            .filter((integration) => !integration.id)
            .map((integration, index) => ({
              tenantId: customer.tenantId,
              calendarId: integration.calendarId,
              calendarProviderId: integration.calendarProviderId,
              resourceEmail: integration.resourceEmail,
              order: integration.order || index + 1,
            })),
          crossOrganizationIntegrationIds:
            removedCrossOrganizationIntegration.map(({ id }) => id),
          // The request layer sends these through the dedicated update mutation
          // and strips this frontend-only field from the room mutation variables.
          crossOrganizationIntegrationUpdates:
            changedCrossOrganizationIntegrations,
          privateCrossOrganizationEvent: values.privateCrossOrganizationEvent,
        };

        await updateRoom(variables);

        if (displaySettingsFormik.dirty) {
          await displaySettingsFormik.submitForm();
        }
      } catch (error: any) {
        console.log(error);
        toast.error(
          error.message
            ? error.message
            : "Cannot update room, please reload and try again!"
        );
      } finally {
        setLoadingSettings(false);
      }
    },
  });

  const savedMainProviderId = roomData?.calendarProviderId || "";
  const savedMainCalendarId = roomData?.calendarId || "";
  const formikMainProviderId = formik.values.calendarProviderId || "";
  const formikMainCalendarId = formik.values.calendarId || "";
  const hasFormikMainCalendar =
    !!formikMainProviderId && !!formikMainCalendarId;
  const hasSavedMainCalendar = !!savedMainProviderId && !!savedMainCalendarId;
  const mainMatchesSaved =
    hasSavedMainCalendar &&
    formikMainProviderId === savedMainProviderId &&
    formikMainCalendarId === savedMainCalendarId;

  const savedFirstCrossOrg =
    roomData?.crossOrganizationIntegration?.[0] || null;
  const formikFirstCrossOrg =
    formik.values.crossOrganizationIntegration?.[0] || null;
  const hasSavedFirstCrossOrg =
    !!savedFirstCrossOrg?.calendarProviderId &&
    !!savedFirstCrossOrg?.calendarId;
  const firstCrossOrgMatchesSaved =
    hasSavedFirstCrossOrg &&
    formikFirstCrossOrg?.calendarProviderId ===
      savedFirstCrossOrg?.calendarProviderId &&
    formikFirstCrossOrg?.calendarId === savedFirstCrossOrg?.calendarId;
  const canAddSecondCrossOrg = mainMatchesSaved && firstCrossOrgMatchesSaved;

  const displaySettingsFormik = useDisplaySettingsFormik(
    roomData?.displaySettings,
    {
      onSubmit: async (values) => {
        try {
          if (!!customerError) {
            toast.error(
              "Failed to fetch working hours, please contact your administrator."
            );
            return;
          }

          setLoadingDisplaySettings(true);

          const { main, display, reservation } = values;

          const variables: UpdateRoomDisplaySettingsVariables = {
            id,
            displaySettings: {
              adminPin: main.adminPin,
              backgroundImageUrl: main.backgroundImageUrl,
              logoImageUrl: main.logoImageUrl,
              layoutTheme: !!main.layoutTheme
                ? {
                    themeVersion: main.layoutTheme.themeVersion,
                    backgroundImageOpacity:
                      main.layoutTheme.backgroundImageOpacity,
                    useColorImage: main.layoutTheme.useColorImage,
                    customHexColors: {
                      busy: main.layoutTheme?.customHexColors?.busy || "",
                      free: main.layoutTheme?.customHexColors?.free || "",
                      tentative:
                        main.layoutTheme?.customHexColors?.tentative || "",
                    },
                  }
                : undefined,
              customerId: user?.customerid || "",
              showOrganizer: display.showOrganizer,
              showMeetingTitle: display.showMeetingTitle,
              showAttendees: display.showAttendees,
              showRoomCapacity: display.showRoomCapacity,
              showRoomAmeneties: display.showRoomAmeneties,
              showCheckinRequired: !!display.showCheckinRequired,
              checkinReminder: display.checkinReminder,
              removeGhostRecurringEvents: display.removeGhostRecurringEvents,
              excludeAllDayEventsFromCheckIn:
                display.excludeAllDayEventsFromCheckIn,
              pendingTimeBeforeMeeting: display.pendingTimeBeforeMeeting,
              pendingTimeAfterMeeting: display.pendingTimeAfterMeeting,
              soonFree: display.soonFree,
              enableScreenLock: display.enableScreenLock,
              screenLock: display.screenLock,
              energySaveMode: display.energySaveMode,
              energySaveModeStart: format(
                dateHelper(
                  reservation.onScreenReservation?.workingHrsEnd || "",
                  "endTime",
                  customerSettings
                ),
                "HH:mm:ss"
              ),
              energySaveModeEnd: format(
                dateHelper(
                  reservation.onScreenReservation?.workingHrsStart || "",
                  "startTime",
                  customerSettings
                ),
                "HH:mm:ss"
              ),
              qrCodeRequired: display.qrCodeRequired,
              contentBoardActivated: display.contentBoardActivated,
              contentBoardURL: display.contentBoardURL,
              textScaling: display.textScaling,
              customLanguage: !!display.customLanguage
                ? {
                    id: display.customLanguage?.id || "",
                    value: display.customLanguage?.value || "",
                    name: display.customLanguage?.name || "",
                    isGlobal: display.customLanguage?.isGlobal || false,
                  }
                : undefined,
              bwFilter: display.bwFilter,

              sensorAutomationEnabled:
                reservation.sensorEntitlement ===
                SensorEntitlement.NO_ENTITLEMENT
                  ? (SensorEntitlement[
                      SensorEntitlement.NO_ENTITLEMENT
                    ] as keyof typeof SensorEntitlement)
                  : reservation.motionSensorAutomation
                    ? (SensorEntitlement[
                        SensorEntitlement.ASSIGNED
                      ] as keyof typeof SensorEntitlement)
                    : (SensorEntitlement[
                        SensorEntitlement.UNASSIGNED
                      ] as keyof typeof SensorEntitlement),
              preset: reservation.motionSensorAutomation
                ? reservation.motionSensorAutomation.preset
                : "custom",
              motionTriggerSensitivity: reservation.motionSensorAutomation
                ? reservation.motionSensorAutomation.motionTriggerSensitivity
                : "responsive",
              autoReleaseDelay: reservation.motionSensorAutomation
                ? reservation.motionSensorAutomation.autoReleaseDelay
                : "cautious",
              vacancyCoolDown: reservation.motionSensorAutomation
                ? reservation.motionSensorAutomation.vacancyCoolDown
                : 5,
              autoBookingDuration: reservation.motionSensorAutomation
                ? reservation.motionSensorAutomation.autoBookingDuration
                : 30,

              onScreenReservation: !!reservation.onScreenReservation,
              showMeetingTitleInput: reservation.onScreenReservation
                ? reservation.onScreenReservation.showMeetingTitleInput
                : false,
              requireMeetingTitleInput: reservation.onScreenReservation
                ? reservation.onScreenReservation.requireMeetingTitleInput
                : false,
              showYourNameInput: reservation.onScreenReservation
                ? reservation.onScreenReservation.showYourNameInput
                : false,
              requireYourNameInput: reservation.onScreenReservation
                ? reservation.onScreenReservation.requireYourNameInput
                : false,
              enforceWorkingHours: reservation.onScreenReservation
                ? reservation.onScreenReservation.enforceWorkingHours
                : false,
              workingHrsActivated: reservation.onScreenReservation
                ? reservation.onScreenReservation.workingHrsActivated
                : false,
              requireHeadcount: reservation.onScreenReservation
                ? reservation.onScreenReservation.requireHeadcount
                : false,
              oneTimePinProtection: reservation.onScreenReservation
                ? reservation.onScreenReservation.oneTimePinProtection
                : false,
              maxDuration: reservation.onScreenReservation?.maxDuration,
              workingWeekDays: customerSettings?.workingWeekDays,
              workingHrsStart: !!reservation.onScreenReservation
                ?.workingHrsStart
                ? isString(reservation.onScreenReservation.workingHrsStart) &&
                  reservation.onScreenReservation.workingHrsStart.length <= 9
                  ? reservation.onScreenReservation.workingHrsStart
                  : format(
                      new Date(
                        reservation.onScreenReservation.workingHrsStart || ""
                      ),
                      "HH:mm:ss"
                    )
                : "",
              workingHrsEnd: !!reservation.onScreenReservation?.workingHrsEnd
                ? isString(reservation.onScreenReservation.workingHrsEnd) &&
                  reservation.onScreenReservation.workingHrsEnd.length <= 9
                  ? reservation.onScreenReservation.workingHrsEnd
                  : format(
                      new Date(
                        reservation.onScreenReservation.workingHrsEnd || ""
                      ),
                      "HH:mm:ss"
                    )
                : "",
              deleteMeeting: reservation.deleteMeeting,
              endMeetingEarly: reservation.endMeetingEarly,
              extendMeeting: reservation.extendMeeting,
              nearbyRooms: reservation.nearbyRooms,
              nearbyEnabled: customerPermissions?.nearbyRooms || false,
              mapView: reservation.mapView,
              floor: !reservation.floor?.id
                ? null
                : {
                    id: reservation.floor.id,
                  },
            },
          };

          await updateRoomDisplay(variables);

          if (formik.dirty) {
            await formik.submitForm();
          }
          toast.success("Display settings updated successfully!");
        } catch (error: any) {
          toast.error(
            error?.message ||
              "Room couldn't be updated, please reload and try again!"
          );
          console.error(error.message);
        } finally {
          setLoadingDisplaySettings(false);
        }
      },
    }
  );

  const devicesList: WorkplaceListData[] = [
    {
      data: roomData?.devices,
      type: "Device",
    },
  ];

  let devicesData = useMemo(() => {
    if (!roomData?.devices) {
      return [
        {
          data: undefined,
          type: "Device",
        },
      ] as WorkplaceListData[];
    }

    if (!!!debouncedValue.length) {
      return devicesList;
    }

    return [
      {
        data: roomData.devices.filter((device) =>
          device.name.toLowerCase().includes(debouncedValue.toLowerCase())
        ),
        type: "Device",
      },
    ] as WorkplaceListData[];
  }, [debouncedValue, roomData?.devices]);

  const routes: SubRouteProps[] = [
    {
      exact: true,
      label: "Devices",
      to: router.match.url,
      id: "devices-page-room",
      render: () => (
        <EditRoomViews
          id={id}
          rows={devicesData}
          searchValue={searchValue}
          debouncedValue={debouncedValue}
          onChange={(e) => setSearchValue(e.target.value)}
          clearSearchValue={() => setSearchValue("")}
        />
      ),
    },
    {
      exact: true,
      label: "Room Settings",
      id: "settings-page-room",
      to: `${router.match.url}/settings`,
      render: () => (
        <EditSettings submitting={loadingSettings} onSubmit={formik.submitForm}>
          <RoomForm
            id={id}
            edit
            formik={formik}
            hasCrossOrgCalendarEntitlement={hasCrossOrgCalendarEntitlement}
            isBussinesOrPro={isBussinesOrPro}
            loading={loading}
            error={error}
            workingHoursData={customerSettings}
            handleWorkingHours={handleWorkingHours}
            defineLicenseNumber={defineLicenseNumber}
            refetch={refetchLicenseNumber}
            canShowCrossOrgSection={hasFormikMainCalendar}
            canEditCrossOrgSection={mainMatchesSaved}
            canAddSecondCrossOrg={canAddSecondCrossOrg}
          />
        </EditSettings>
      ),
    },
    {
      exact: true,
      label: "Display Settings",
      id: "display-settings-page-room",
      to: `${router.match.url}/display-settings`,
      render: () => (
        <EditSettings
          splitSections
          onSubmit={displaySettingsFormik.submitForm}
          submitting={loadingDisplaySettings}
          // hasChanges={
          //   displaySettingsFormik.dirty && displaySettingsFormik.isValid
          // }
        >
          <DisplaySettingsForm
            roomId={id}
            formik={displaySettingsFormik}
            isBussinesOrPro={isBussinesOrPro}
            error={error}
            loading={loading}
            workingHoursData={customerSettings}
            refetch={refetch}
            currentCustomLanguageId={
              roomData?.displaySettings?.customLanguage?.id
            }
            displayEditWorkHours={
              roomData?.displaySettings?.workingHrsActivated
            }
            downloadQRicon={
              roomData ? (
                <DownloadQRCode
                  name={roomData?.name}
                  type={ListTagsEntity.TYPE_ROOM}
                  id={roomData?.id}
                />
              ) : (
                <></>
              )
            }
          />
        </EditSettings>
      ),
    },
  ];

  return (
    <EditWorkplaceWrapper
      type="Room"
      id={id}
      routes={routes}
      data={roomData || undefined}
      message={tooltips.workplaces.room}
      numberOfDeviceAdded={roomData?.devices?.length}
      loading={loadingRoomData}
    />
  );
};
